import { Fragment, useEffect } from "react";
import { capitalise, dedupe } from "lib/useful";
import { collect, store } from "react-recollect";
import useStoreLoader from "lib/useStoreLoader";
import Loading from "components/Loading";
import OrderLine from "components/OrderLine";
import { certPOST } from "lib/API";
import Page from "components/Page";
import FullsizeMessage from "components/FullsizeMessage";
import RenderFieldReissueSearch from "components/RenderFieldReissueSearch";
import { ChevronDoubleRightIcon } from "@heroicons/react/outline";
import { Link } from "react-router-dom";
import ButtonWithConfirmation from "components/ButtonWithConfirmation";
import LoadResearches from "components/loaders/LoadResearches";
import LoadResearchData from "components/loaders/LoadResearchData";
import CertSummary from "components/CertSummary";
import LoadShopifyOrders, {
  needToLoadShopifyOrders,
} from "components/loaders/LoadShopifyOrders";
import CopyToClipboard from "components/CopyToClipboard";

function StandaloneList({ type, text }) {
  const { isLoading, items } = useStoreLoader({
    collection: `standalone`,
    storeKey: `standalone-${type}`,
    find: { type, completed: null },
  });
  useEffect(
    () => () => {
      delete store[`standalone-${type}`];
      delete store["certificateResearch"];
      delete store["researchDataByCertId"];
    },
    [type],
  ); // force it to reload next time

  const standaloneItems = Object.values(items ?? {});

  const researchesToLoad = dedupe(
    standaloneItems.flatMap((sa) =>
      !store.certificateResearch?.[sa.linkedresearchId]
        ? sa.linkedresearchId || []
        : [],
    ),
  );
  const researchDataToLoad = dedupe(
    standaloneItems.flatMap((sa) =>
      !store.researchDataByCertId?.[sa.linkedresearchId]
        ? sa.linkedresearchId || []
        : [],
    ),
  );
  const shopifyOrdersToLoad = dedupe(
    standaloneItems.flatMap((sa) =>
      !store.shopifyOrders?.[sa.shopifyOrdersId]
        ? sa.shopifyOrdersId || []
        : [],
    ),
  );
  console.log({ researchesToLoad, researchDataToLoad, shopifyOrdersToLoad });
  const nowDate = new Date();

  return (
    <Page title={capitalise(type)} heading={`To be ${text}`}>
      {isLoading ? (
        <Loading text={`Loading items ready to be ${text}`} />
      ) : needToLoadShopifyOrders(shopifyOrdersToLoad) ? (
        <LoadShopifyOrders ids={shopifyOrdersToLoad} />
      ) : researchesToLoad.length > 0 ? (
        <LoadResearches ids={researchesToLoad} />
      ) : researchDataToLoad.length > 0 ? (
        <LoadResearchData ids={researchDataToLoad} mightBeEmpty />
      ) : standaloneItems.length === 0 ? (
        <FullsizeMessage>No orders waiting to be {text}</FullsizeMessage>
      ) : (
        <>
          <div>
            {standaloneItems?.map((standaloneItem) => {
              if (standaloneItem?.complete)
                return <p>Standalone item {standaloneItem?._id} is complete</p>;
              const certStatus =
                store.certificateResearch?.[standaloneItem?.linkedresearchId]
                  ?.status;
              const certNumber =
                store.researchDataByCertId[standaloneItem?.linkedresearchId]
                  ?.certNumber;

              const shipping_address =
                store.shopifyOrders?.[standaloneItem?.shopifyOrdersId]
                  ?.shipping_address;
              return (
                <OrderLine key={standaloneItem?._id} r={standaloneItem}>
                  <div className="mb-2">
                    Part of order {standaloneItem?.name}:{" "}
                    <a
                      className="text-slate-700 hover:underline mb-2"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://certificates-3.myshopify.com/admin/orders/${standaloneItem?.shopifyId}`}
                    >
                      Open in Shopify
                    </a>
                    {" | "}
                    <Link
                      className="text-slate-700 hover:underline mb-2"
                      to={`/search/#${btoa(standaloneItem?.name)}`}
                    >
                      Search
                    </Link>
                  </div>

                  {[
                    "techchecking",
                    "fulfiling",
                    "complete",
                    "oldCert",
                  ].includes(certStatus) ? (
                    <>
                      <CertSummary
                        cert={
                          store.researchDataByCertId[
                            standaloneItem?.linkedresearchId
                          ]
                        }
                      >
                        {standaloneItem?.customerInfo?.map(
                          ({ name, value }) => {
                            if (name === "_vin") return null;
                            if (name === "_customerData") return null;
                            if (name === "previous_certificate_number")
                              return (
                                <Fragment key={name}>
                                  <p>
                                    Previous certificate number (from customer):{" "}
                                    <b>{value}</b>
                                  </p>
                                  <RenderFieldReissueSearch
                                    initialSearch={value}
                                  />
                                </Fragment>
                              );
                            if (name === "notes")
                              return (
                                <div key={name}>
                                  Notes (from customer):
                                  <div className="border m-1 p-1 whitespace-pre-line">
                                    {value}
                                  </div>
                                </div>
                              );
                            return (
                              <pre key={name}>
                                {JSON.stringify({ name, value }, null, 2)}
                              </pre>
                            );
                          },
                        )}
                      </CertSummary>
                      <br />
                      {standaloneItem?.customerInfo?.map(({ name, value }) => {
                        if (name === "notes")
                          return (
                            <div key={name} className="mt-2">
                              <h2 className="text-2xl">
                                Notes (from customer)
                              </h2>
                              <div className="border p-2">{value}</div>
                            </div>
                          );

                        if (name === "previous_certificate_number")
                          return (
                            <RenderFieldReissueSearch
                              key={name}
                              leftText="Customer-supplied certificate number"
                              initialSearch={value}
                              withCopyButton={false}
                            />
                          );

                        return null;
                      })}

                      {shipping_address ? (
                        <div className="mt-2">
                          <h2 className="text-2xl">Shipping address</h2>
                          <CopyToClipboard
                            text={[
                              "name",
                              "address1",
                              "address2",
                              "city",
                              "province",
                              "zip",
                              "country",
                            ]
                              .flatMap((k) => shipping_address[k] ?? [])
                              .join("\n")}
                          />
                        </div>
                      ) : (
                        <>
                          <p className="text-red-400">
                            Can't find shipping address from Shopify.
                          </p>
                          <p>Here is the billing address instead:</p>
                          <CopyToClipboard
                            text={[
                              "name",
                              "address1",
                              "address2",
                              "city",
                              "province",
                              "zip",
                              "country",
                            ]
                              .flatMap(
                                (k) =>
                                  store.shopifyOrders[
                                    standaloneItem?.shopifyOrdersId
                                  ].billing_address[k] ?? [],
                              )
                              .join("\n")}
                          />
                        </>
                      )}

                      <h2 className="text-2xl">Email</h2>
                      <p>
                        {store.shopifyOrders[standaloneItem?.shopifyOrdersId]
                          ?.email ?? "No email found!?"}
                      </p>
                    </>
                  ) : (
                    <h3 className="text-xl font-bold">
                      {certStatus
                        ? `Certificate${certNumber ? ` (${certNumber})` : ""} is ${certStatus}`
                        : "Can't find any certificate data yet."}
                    </h3>
                  )}

                  <ButtonWithConfirmation
                    message={`Really complete ${standaloneItem?.name}?`}
                    onConfirm={async () => {
                      await certPOST("standaloneComplete", {
                        _id: standaloneItem?._id,
                      });
                      standaloneItem.complete = nowDate;
                    }}
                    className="mt-4 ml-2"
                    color="blue"
                  >
                    <ChevronDoubleRightIcon className="size-6" />
                    &nbsp;Complete
                  </ButtonWithConfirmation>
                </OrderLine>
              );
            })}
          </div>
        </>
      )}
    </Page>
  );
}

export default collect(StandaloneList);
